import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/utils/translations';
import {
  Stethoscope,
  Gem,
  SmilePlus,
  Sparkles,
  Target,
  Microscope,
  Shield,
  Moon,
  Leaf,
  Sun,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const SERVICE_ICONS = [
  Stethoscope, // Odontología General
  Gem,         // Prostodoncia
  SmilePlus,   // Ortopedia y Ortodoncia
  Sparkles,    // Estética Dental y Orofacial
  Target,      // Implantología
  Microscope,  // Endodoncia
  Shield,      // Periodoncia
  Sun,         // Disfunción Craneomandibular
  Moon,        // Ronquido y Apneas
  Leaf,        // Formación Holística
];

export default function Services() {
  const { language } = useLanguage();
  const t = translations[language].services;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi?.scrollTo(i), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi?.off('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section id="services" className="py-20 md:py-32 bg-[#e3e6e8]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#5b6770] mb-4">{t.title}</h2>
          <p className="text-[#5b6770] text-lg max-w-2xl mx-auto">{t.subtitle}</p>
        </motion.div>

        <div className="relative">
          <div ref={emblaRef} className="overflow-hidden" data-testid="services-carousel">
            <div className="flex -ml-4">
              {t.items.map((item, i) => {
                const Icon = SERVICE_ICONS[i];
                return (
                  <div
                    key={i}
                    className="min-w-0 shrink-0 grow-0 basis-full sm:basis-1/2 lg:basis-1/3 pl-4"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.04 }}
                      className="bg-white rounded-2xl border border-gray-100 p-7 h-full hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 group"
                      data-testid={`service-card-${i}`}
                    >
                      <div className="w-12 h-12 bg-[#fff6e5] rounded-xl flex items-center justify-center mb-5 shadow-sm group-hover:bg-[#ffa600] transition-all duration-300">
                        <Icon className="w-5 h-5 text-[#ffa600] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#5b6770] mb-2">{item.name}</h3>
                      <p className="text-[#5b6770] text-sm leading-relaxed">{item.desc}</p>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            onClick={scrollPrev}
            className="absolute -left-3 md:-left-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-md border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
            data-testid="services-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-[#5b6770]" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute -right-3 md:-right-5 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-md border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
            data-testid="services-next"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-[#5b6770]" />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-8" data-testid="services-dots">
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === selectedIndex ? 'bg-[#ffa600] w-6' : 'bg-[#b8c0c7] w-2'
              }`}
              data-testid={`services-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
